import { useState, useEffect } from "react";
import { X, Sliders, MapPin, Newspaper, Check } from "lucide-react";

const LS_KEY = "newseze.prefs.v1";

const CATEGORIES = [
  { id: "local", label: "Local" },
  { id: "national", label: "National" },
  { id: "world", label: "World" },
  { id: "politics", label: "Politics" },
  { id: "business", label: "Business & Markets" },
  { id: "sports", label: "Sports" },
  { id: "technology", label: "Tech & AI" },
  { id: "entertainment", label: "Entertainment" },
  { id: "lifestyle", label: "Lifestyle" },
  { id: "investigations", label: "Investigations" },
];

const LOCATIONS = [
  { id: "phoenix", label: "Phoenix" },
  { id: "san-diego", label: "San Diego" },
  { id: "los-angeles", label: "Los Angeles" },
  { id: "new-york", label: "New York" },
  { id: "miami", label: "Miami" },
  { id: "dallas", label: "Dallas" },
  { id: "atlanta", label: "Atlanta" },
  { id: "chicago", label: "Chicago" },
  { id: "denver", label: "Denver" },
  { id: "seattle", label: "Seattle" },
  { id: "us", label: "United States" },
  { id: "global", label: "Global" },
];

export function loadPrefs() {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) || defaultPrefs(); }
  catch { return defaultPrefs(); }
}
export function savePrefs(p) {
  localStorage.setItem(LS_KEY, JSON.stringify(p));
  window.dispatchEvent(new CustomEvent("newseze:prefs-changed", { detail: p }));
}
function defaultPrefs() {
  // perspective intentionally omitted — Newseze chooses tone, readers choose subjects.
  return { categories: ["local", "national", "politics", "business"], locations: [] };
}

export default function PersonalizationModal({ open, onClose }) {
  const [p, setP] = useState(loadPrefs);
  useEffect(() => { if (open) setP(loadPrefs()); }, [open]);

  if (!open) return null;

  const toggle = (key, val) => {
    setP((cur) => {
      const list = new Set(cur[key] || []);
      list.has(val) ? list.delete(val) : list.add(val);
      return { ...cur, [key]: Array.from(list) };
    });
  };

  const handleSave = () => { savePrefs(p); onClose?.(); };

  return (
    <div className="fixed inset-0 z-[100] bg-black/55 backdrop-blur-sm flex items-center justify-center px-4 py-6" onClick={onClose} data-testid="prefs-modal-backdrop">
      <div className="bg-white w-full max-w-[640px] max-h-[88vh] overflow-y-auto rounded-2xl shadow-2xl border border-[var(--line)]" onClick={(e) => e.stopPropagation()} data-testid="prefs-modal">
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-[var(--line)] z-10">
          <div className="flex items-center gap-2">
            <span className="inline-flex w-8 h-8 rounded-md bg-[var(--gold)] items-center justify-center"><Sliders size={14} className="text-white" /></span>
            <div>
              <div className="text-[15px] font-black tracking-tight">Personalize my Newseze</div>
              <div className="text-[11px] text-[var(--muted)]">The home page rearranges automatically · saved on this device</div>
            </div>
          </div>
          <button className="text-[var(--muted)] hover:text-black" onClick={onClose} data-testid="prefs-close"><X size={18} /></button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Favorite Categories — subjects only, no partisan lanes */}
          <section>
            <div className="flex items-center gap-2 mb-2.5">
              <Newspaper size={14} className="text-[var(--gold)]" />
              <h3 className="text-[12px] uppercase tracking-[0.18em] font-black text-[var(--ink)]">Favorite Categories</h3>
              <span className="text-[11px] text-[var(--muted)]">— shown first in your feed</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => {
                const on = (p.categories || []).includes(c.id);
                return (
                  <button key={c.id} onClick={() => toggle("categories", c.id)} className={`text-[12px] font-bold px-3 py-1.5 rounded-md transition ${on ? "bg-[var(--ink)] text-white" : "bg-white border border-[var(--line)] text-[var(--ink)] hover:border-[var(--blue)]"}`} data-testid={`prefs-cat-${c.id}`}>
                    {on && "✓ "}{c.label}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Favorite Locations */}
          <section>
            <div className="flex items-center gap-2 mb-2.5">
              <MapPin size={14} className="text-[var(--gold)]" />
              <h3 className="text-[12px] uppercase tracking-[0.18em] font-black text-[var(--ink)]">Favorite Locations</h3>
              <span className="text-[11px] text-[var(--muted)]">— for Local + Near You</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {LOCATIONS.map((l) => {
                const on = (p.locations || []).includes(l.id);
                return (
                  <button key={l.id} onClick={() => toggle("locations", l.id)} className={`text-[12px] font-bold px-3 py-1.5 rounded-md transition ${on ? "bg-[#0e7490] text-white" : "bg-white border border-[var(--line)] text-[var(--ink)] hover:border-[var(--blue)]"}`} data-testid={`prefs-loc-${l.id}`}>
                    {on && "✓ "}{l.label}
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[var(--line)] px-6 py-3 flex items-center justify-between">
          <button onClick={() => setP(defaultPrefs())} className="text-[12px] text-[var(--muted)] hover:text-black font-bold" data-testid="prefs-reset">Reset to default</button>
          <button onClick={handleSave} className="btn gold" data-testid="prefs-save"><Check size={13} /> Save & apply</button>
        </div>
      </div>
    </div>
  );
}
